import { IFeedLeague, IFeedTeam, IFeedTodayFilters } from '@/types';
import { EventFeedType, EventPeriod, League, Line, StoreName } from '@/constants';
import { useSearchedTeamIds } from '@/stores/composables/feed/useSearchedTeamIds';
import { useSearchedLeagueIds } from '@/stores/composables/feed/useSearchedLeagueIds';
import { useBetsChangedHandler } from '@/stores/composables/feed/useBetsChangedHandler';
import { useFeedResponseProcessor } from '@/stores/composables/feed/useFeedResponseProcessor';
import { FeedPregameItem } from '@/models/feedPregame';
import { usePersistentFilters } from '@/stores/composables/usePersistentFilters';
import { getMinDateForEarlyFeed } from '@/helpers';

export const useFeedPregameStore = defineStore(StoreName.FeedPregame, () => {
    const todayItems = shallowRef<FeedPregameItem[]>([]);
    const earlyItems = shallowRef<FeedPregameItem[]>([]);
    const todayVersion = ref<number>(0);
    const earlyVersion = ref<number>(0);
    const todayEventsPositionIdx = ref<Record<string, number>>({});
    const earlyEventsPositionIdx = ref<Record<string, number>>({});
    const total = ref<number>(0);
    const loading = ref<boolean>(false);
    const isExtraFiltersDisplayed = ref<boolean>(false);
    const customColumns = ref<string[] | null>(null);
    const filters = reactive<IFeedTodayFilters>({
        search: '',
        line: Line.Main,
        customLeagueId: null,
        eventFeedTypeId: EventFeedType.Today,
        periodTypeId: EventPeriod.FullTime,
        customEventId: null,
        isPositionOnly: false,
        isFantasyExclude: false,
        date: getMinDateForEarlyFeed(),
    });

    usePersistentFilters(filters, ['isPositionOnly', 'isFantasyExclude']);

    const leagueItems = ref<Record<number, IFeedLeague>>({});
    const teamItems = ref<Record<number, IFeedTeam>>({});

    function reset() {
        todayItems.value = [];
        earlyItems.value = [];
        todayVersion.value = 0;
        earlyVersion.value = 0;
        todayEventsPositionIdx.value = {};
        earlyEventsPositionIdx.value = {};
        leagueItems.value = {};
        teamItems.value = {};
    }

    const { checkResponse: checkTodayResponse, processResponse: processTodayResponse } = useFeedResponseProcessor<FeedPregameItem>(
        EventFeedType.Today,
        todayItems,
        filters,
        'FeedPregameItem',
        todayEventsPositionIdx,
        todayVersion,
        leagueItems,
        teamItems
    );

    const { checkResponse: checkEarlyResponse, processResponse: processEarlyResponse } = useFeedResponseProcessor<FeedPregameItem>(
        EventFeedType.Early,
        earlyItems,
        filters,
        'FeedPregameItem',
        earlyEventsPositionIdx,
        earlyVersion,
        leagueItems,
        teamItems
    );

    const searchRef = toRef(filters, 'search');
    const { searchedTeamIds } = useSearchedTeamIds(searchRef, teamItems);
    const { searchedLeagueIds } = useSearchedLeagueIds(searchRef, leagueItems);

    useBetsChangedHandler(todayItems);

    const displayedItems = shallowRef<FeedPregameItem[]>([]);
    const displayedItemsLength = ref<number>(0);

    //we use watch because need to exclude line, customEventId from update initiators
    watch(
        [
            todayItems,
            earlyItems,
            // filteredLeagueIds,
            //() => filters.customEventId,
            // () => filters.customLeagueId,
            () => [...(filters.customLeagueId ?? [])],
            () => filters.isPositionOnly,
            () => filters.search,
            () => filters.isFantasyExclude,
            searchedLeagueIds,
            searchedTeamIds,
            //() => filters.line // no need run update if line changed. If player changes line filter, will be new events, only after that we should update displayedItems
        ],
        () => {
            const activeItems = filters.eventFeedTypeId === EventFeedType.Today ? todayItems.value : earlyItems.value;

            console.log('[watch] pregameDisplayedItems. Total events count: ', activeItems.length);

            let groupsCount = 0;
            const out: FeedPregameItem[] = [];
            let lastLeagueId = 0;
            for (let i = 0; i < activeItems.length; i++) {
                const item = activeItems[i];

                const pass =
                    //we should filter by line, because event can have all lines(for betting popup for example)
                    (filters.line >= item.line.value || (filters.customEventId !== null && filters.customEventId === item.customEventId.value)) &&
                    // (!filteredLeagueIds.value || filteredLeagueIds.value.includes(item.leagueId.value)) &&
                    (!filters.customLeagueId || filters.customLeagueId.includes(item.leagueId.value)) &&
                    (!filters.isPositionOnly || item.position.value !== null) &&
                    (!filters.isFantasyExclude || item.leagueId.value !== League.Fantasy) &&
                    (!filters.search ||
                        filters.eventFeedTypeId === EventFeedType.Early ||
                        searchedLeagueIds.value!.includes(item.leagueId.value) ||
                        searchedTeamIds.value!.includes(item.teamHomeId.value) ||
                        searchedTeamIds.value!.includes(item.teamAwayId.value));

                if (!pass) continue;

                if (lastLeagueId !== item.leagueId.value) {
                    item.isNewGroupStarted.value = 1;
                    lastLeagueId = item.leagueId.value;
                    groupsCount++;
                } else {
                    item.isNewGroupStarted.value = 0;
                }

                out.push(item);
            }

            displayedItems.value = out;
            displayedItemsLength.value = displayedItems.value.length + groupsCount;

            console.time(`Render pregame ${displayedItems.value.length} items`);
            nextTick().then(() => console.timeEnd(`Render pregame ${displayedItems.value.length} items`));
        }
    );

    /*const displayedItems = computed<FeedPregameItem[]>(() => {
        const activeItems = filters.eventFeedTypeId === EventFeedType.Today ? todayItems.value : earlyItems.value;

        console.log('[computed] pregameDisplayedItems. Total events count: ', activeItems.length);

        const filteredItems = activeItems.filter((item) => {
            return (
                //we should filter by line, because event can have all lines(for betting popup for example)
                (filters.line! >= item.line.value || (filters.customEventId !== null && filters.customEventId === item.customEventId.value)) &&
                (!filters.isPositionOnly || item.position.value !== null) &&
                (!filters.isFantasyExclude || item.leagueId.value !== League.Fantasy) &&
                (!filters.search ||
                    filters.eventFeedTypeId === EventFeedType.Early ||
                    searchedLeagueIds.value!.includes(item.leagueId.value) ||
                    searchedTeamIds.value!.includes(item.teamHomeId.value) ||
                    searchedTeamIds.value!.includes(item.teamAwayId.value))
            );
        }) as FeedPregameItem[];

        console.time(`Render pregame ${filteredItems.length} items`);
        nextTick().then(() => console.timeEnd(`Render pregame ${filteredItems.length} items`));
        return filteredItems;
    });*/

    return {
        checkEarlyResponse,
        checkTodayResponse,
        customColumns,
        displayedItems,
        displayedItemsLength,
        earlyVersion,
        filters,
        isExtraFiltersDisplayed,
        leagueItems,
        loading,
        processEarlyResponse,
        processTodayResponse,
        reset,
        teamItems,
        todayVersion,
        total,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useFeedPregameStore, import.meta.hot));
}
